import type { ReactNode } from "react";
import { cn } from "./cn";

export const CONTROL_SIZES = ["xs", "sm", "md", "lg"] as const;

export type ControlSize = (typeof CONTROL_SIZES)[number];

export const CONTROL_SIZE_CLASSES = {
  xs: "h-7 px-2 text-[11px]",
  sm: "h-8 px-2.5 text-xs",
  md: "h-10 px-3 text-xs",
  lg: "h-12 px-4 text-sm",
} as const satisfies Record<ControlSize, string>;

export const CONTROL_MIN_HEIGHT_CLASSES = {
  xs: "min-h-7 px-2 text-[11px]",
  sm: "min-h-8 px-2.5 text-xs",
  md: "min-h-10 px-3 text-xs",
  lg: "min-h-12 px-4 text-sm",
} as const satisfies Record<ControlSize, string>;

export const CONTROL_BASE_CLASSES =
  "w-full rounded-lg border bg-app text-content transition-colors";

const CONTROL_STATE_CLASSES = {
  normal:
    "border-line hover:border-line-soft focus:border-primary-500 focus:ring-primary-500/30",
  invalid: "border-error focus:border-error focus:ring-error/30",
} as const satisfies Record<string, string>;

export const CONTROL_REST_CLASSES = {
  normal: "border-line hover:border-line-soft",
  invalid: "border-error",
} as const satisfies Record<string, string>;

export const CONTROL_FOCUS_CLASSES = {
  normal: "border-primary-500 ring-2 ring-primary-500/30",
  invalid: "border-error ring-2 ring-error/30",
} as const satisfies Record<string, string>;

export function controlClasses(
  hasError: boolean | undefined,
  size: ControlSize,
): string {
  return cn(
    CONTROL_BASE_CLASSES,
    "placeholder:text-content-faint",
    "focus:outline-none focus:ring-2",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "read-only:bg-surface",
    CONTROL_STATE_CLASSES[hasError ? "invalid" : "normal"],
    CONTROL_SIZE_CLASSES[size],
  );
}

export interface FieldShellProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label ? (
        <label htmlFor={id} className="text-sm font-medium text-content">
          {label}
          {required ? <span className="ml-0.5 text-error">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function describedBy(
  id: string,
  hasError: boolean,
  hasHint: boolean,
): string | undefined {
  if (hasError) return `${id}-error`;
  if (hasHint) return `${id}-hint`;
  return undefined;
}
